/**
 * All navigation, on a phone, behind one control.
 *
 * Above the phone breakpoint this renders nothing at all and the topbar keeps the
 * shape it has always had: crumbs, account, sign out, laid out across a line that is
 * wide enough for them. Below it that line is not wide enough, and the honest
 * arrangement is not a smaller version of the same row — it is one row and a menu.
 * Measured on a 393px viewport before this existed, the topbar and the workspace's
 * own tab strip together took 237px of an 800px viewport, and neither of them was
 * what the reader had come to look at.
 *
 * What goes in: everything that moves you somewhere. Dataset crumbs, the current
 * route's own views (published through `state/viewtabs`, because the shell must not
 * hold a route's state to be able to show it), the admin routes, and the account.
 *
 * What stays out: the evidence opener. It is not navigation — it is the way back to
 * the quote behind the citation the reader tapped a second ago, and two taps to reach
 * it is the difference between checking a source and taking it on trust. It keeps its
 * place in the topbar, on the left beside the brand, deliberately far from this
 * button: it comes and goes with the selection, and an intermittent control adjacent
 * to the hamburger would keep changing what is under a thumb that is reaching for the
 * hamburger. Reaching for navigation and getting a sheet of evidence over the answer
 * is the more expensive of the two mistakes.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { Principal } from "../lib/api";
import { useSheet } from "../lib/sheet";
import { useViewTabs } from "../state/viewtabs";

export function TopbarMenu({
  datasetId,
  principal,
  onSignOut,
}: {
  /** Present exactly when the route has one, and the dataset crumbs with it. */
  datasetId: string | undefined;
  principal: Principal;
  onSignOut(): void;
}) {
  const [open, setOpen] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);
  const sheet = useSheet(panel, open, close, toggle);
  const tabs = useViewTabs();

  // A rotation into landscape puts the topbar back to full width, where this control
  // does not exist. An open menu left behind would be a modal nobody can dismiss and
  // a body left with `overflow: hidden`; the hook's own cleanup handles the second,
  // this handles the first — and the state must not survive to reopen the menu on
  // the way back.
  useEffect(() => {
    if (!sheet) setOpen(false);
  }, [sheet]);

  // Every link in here closes the menu on its way out, so this is not for them. It is
  // for the phone's own back gesture, which is the first thing a thumb reaches for to
  // dismiss something — and which navigates instead. Getting the previous route with
  // the menu still standing over it is the one outcome that would be read as broken.
  useEffect(() => {
    if (!open) return;
    const dismiss = () => setOpen(false);
    window.addEventListener("hashchange", dismiss);
    return () => window.removeEventListener("hashchange", dismiss);
  }, [open]);

  if (!sheet) return null;

  return (
    <>
      <button
        ref={toggle}
        className="menu-toggle"
        data-testid="menu-toggle"
        aria-label="Navigation"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="topbar-menu"
        onClick={() => setOpen((showing) => !showing)}
      >
        <span aria-hidden="true">☰</span>
      </button>

      {open && (
        <div
          className="sheet-backdrop"
          data-testid="menu-backdrop"
          aria-hidden="true"
          onClick={close}
        />
      )}

      {open && (
        <div
          id="topbar-menu"
          className="topbar-menu"
          data-testid="topbar-menu"
          ref={panel}
          role="dialog"
          aria-modal="true"
          aria-label="Navigation"
          tabIndex={-1}
        >
          {datasetId && (
            <div className="menu-group">
              <span className="menu-label">Dataset</span>
              <a href={`#/d/${datasetId}`} data-testid="nav-workspace" onClick={close}>
                Workspace
              </a>
              <a href={`#/d/${datasetId}/files`} data-testid="nav-files" onClick={close}>
                Files
              </a>
            </div>
          )}

          {tabs.length > 0 && (
            <div className="menu-group">
              <span className="menu-label">View</span>
              {tabs.map((tab) => (
                <button
                  key={tab.testid}
                  data-testid={tab.testid}
                  // `aria-current` and not `aria-selected`: outside a tablist there is
                  // no tablist for a tab to be selected in, and the strip these came
                  // from is not rendered at this width. An entry with no selected
                  // state is not a view but an overlay opener, and says so instead.
                  aria-current={tab.selected ?? undefined}
                  aria-haspopup={tab.selected === null ? "dialog" : undefined}
                  onClick={() => {
                    tab.choose();
                    close();
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          {principal.is_admin && (
            <div className="menu-group">
              <span className="menu-label">Administration</span>
              <a href="#/settings" data-testid="nav-settings" onClick={close}>
                Providers
              </a>
              <a href="#/audit" data-testid="nav-audit" onClick={close}>
                Audit
              </a>
            </div>
          )}

          <div className="menu-group">
            <span className="menu-label">Account</span>
            <span className="mono menu-principal" data-testid="principal">
              {principal.email}
            </span>
            <button data-testid="logout" onClick={onSignOut}>
              Sign out
            </button>
          </div>
        </div>
      )}
    </>
  );
}
